import type {
  ExistingTreeRepairPlan,
} from './ExistingTreeRepairPlan';
import type {
  ExistingTreeRepairPlanGrouping,
} from './ExistingTreeRepairPlanGrouping';
import type {
  ExistingTreeRepairPlanSummary,
} from './ExistingTreeRepairPlanSummary';

export type ExistingTreeRendererCertificationStatus =
  | 'WAITING_FOR_LIVE_PROJECTION'
  | 'LIVE_REVIEW_VISIBLE'
  | 'LIVE_REVIEW_INCOMPLETE';

export interface ExistingTreeRepairRendererCertification {
  status: ExistingTreeRendererCertificationStatus;
  liveProjectionReceived: boolean;
  summaryVisible: boolean;
  groupingVisible: boolean;
  detailedItemsVisible: boolean;
  memberNamesVisible: boolean;
  approvalsVisible: boolean;
  relatedIdsRepresented: boolean;
  safetyBoundaryVisible: true;
  certificationAllowed: false;
  executionAllowed: false;
  productionWritesAllowed: false;
  reasons: string[];
}

export function certifyExistingTreeRepairRenderer(
  summary: ExistingTreeRepairPlanSummary | null,
  grouping: ExistingTreeRepairPlanGrouping | null,
  plan: ExistingTreeRepairPlan | null,
): ExistingTreeRepairRendererCertification {
  const liveProjectionReceived = Boolean(
    summary || grouping || plan,
  );

  const summaryVisible = Boolean(summary);

  const groupingVisible = Boolean(
    grouping?.categoryGroups,
  );

  const items = plan?.items ?? [];

  const detailedItemsVisible =
    items.length > 0;

  const memberNamesVisible =
    items.length > 0 &&
    items.every(
      (item) => item.memberName.trim().length > 0,
    );

  const approvalsVisible =
    items.length > 0 &&
    items.every(
      (item) =>
        item.requiredApprovals.length > 0,
    );

  const relatedIdsRepresented =
    items.length > 0 &&
    items.every(
      (item) => Array.isArray(item.relatedIds),
    );

  const reasons: string[] = [];

  if (!liveProjectionReceived) {
    reasons.push(
      'No live Tree repair-plan projection has reached the renderer.',
    );
  }

  if (liveProjectionReceived && !summaryVisible) {
    reasons.push(
      'The live repair-plan summary is not available.',
    );
  }

  if (liveProjectionReceived && !groupingVisible) {
    reasons.push(
      'The live repair-plan grouping is not available.',
    );
  }

  if (
    liveProjectionReceived &&
    plan &&
    plan.itemCount > 0 &&
    !detailedItemsVisible
  ) {
    reasons.push(
      'The repair plan reports findings but detailed items are not visible.',
    );
  }

  if (
    detailedItemsVisible &&
    !memberNamesVisible
  ) {
    reasons.push(
      'One or more projected repair items does not expose a member name.',
    );
  }

  if (
    detailedItemsVisible &&
    !approvalsVisible
  ) {
    reasons.push(
      'One or more projected repair items does not expose required approvals.',
    );
  }

  if (
    detailedItemsVisible &&
    !relatedIdsRepresented
  ) {
    reasons.push(
      'One or more projected repair items does not represent related IDs.',
    );
  }

  const complete =
    liveProjectionReceived &&
    summaryVisible &&
    groupingVisible &&
    (
      plan?.itemCount === 0 ||
      (
        detailedItemsVisible &&
        memberNamesVisible &&
        approvalsVisible &&
        relatedIdsRepresented
      )
    );

  return {
    status: !liveProjectionReceived
      ? 'WAITING_FOR_LIVE_PROJECTION'
      : complete
        ? 'LIVE_REVIEW_VISIBLE'
        : 'LIVE_REVIEW_INCOMPLETE',
    liveProjectionReceived,
    summaryVisible,
    groupingVisible,
    detailedItemsVisible,
    memberNamesVisible,
    approvalsVisible,
    relatedIdsRepresented,
    safetyBoundaryVisible: true,
    certificationAllowed: false,
    executionAllowed: false,
    productionWritesAllowed: false,
    reasons,
  };
}
